import React, { useState, useEffect, useMemo } from 'react'
import { X, Pencil, Trash2, Search } from 'lucide-react'
import {
  OrgChartService,
  RoleDefinition,
  RoleDefinitionUsage
} from '../../services/orgChartService'
import { EditRoleDefinitionModal } from './EditRoleDefinitionModal'

interface ManageRolesModalProps {
  isOpen: boolean
  onClose: () => void
  onChanged: () => Promise<void> | void
}

export const ManageRolesModal: React.FC<ManageRolesModalProps> = ({
  isOpen,
  onClose,
  onChanged
}) => {
  const [usage, setUsage] = useState<RoleDefinitionUsage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [unusedOnly, setUnusedOnly] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [editingDefinition, setEditingDefinition] = useState<RoleDefinition | null>(null)
  const [editOpen, setEditOpen] = useState(false)

  const orgChartService = OrgChartService.getInstance()

  const loadUsage = async (opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true)
      setError('')
      const data = await orgChartService.getRoleDefinitionUsage()
      setUsage(data)
    } catch (err: any) {
      console.error('Error loading role usage:', err)
      setError(err.message || 'Failed to load roles')
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setSearch('')
      setUnusedOnly(false)
      loadUsage()
    }
  }, [isOpen])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return usage.filter(row => {
      if (unusedOnly && row.seatCount > 0) return false
      if (!q) return true
      return row.definition.name.toLowerCase().includes(q)
    })
  }, [usage, search, unusedOnly])

  const unusedCount = usage.filter(r => r.seatCount === 0).length

  const handleDelete = async (row: RoleDefinitionUsage) => {
    if (row.seatCount > 0) return
    if (!confirm(`Delete role “${row.definition.name}”? This cannot be undone.`)) {
      return
    }

    try {
      setDeletingId(row.definition.id)
      setError('')
      // Remove immediately so the list doesn't blank while refreshing
      setUsage(prev => prev.filter(r => r.definition.id !== row.definition.id))
      await orgChartService.deleteRoleDefinition(row.definition.id)
      await onChanged()
    } catch (err: any) {
      console.error('Error deleting role:', err)
      setError(err.message || 'Failed to delete role')
      await loadUsage({ silent: true })
    } finally {
      setDeletingId(null)
    }
  }

  const handleEditSaved = async (definitionId: string | null) => {
    const editedId = editingDefinition?.id
    setEditOpen(false)
    setEditingDefinition(null)
    if (definitionId === null && editedId) {
      // Deleted from edit modal — drop from list without a loading flash
      setUsage(prev => prev.filter(r => r.definition.id !== editedId))
    }
    await loadUsage({ silent: true })
    await onChanged()
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Manage Roles</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {usage.length} role{usage.length === 1 ? '' : 's'}
                {unusedCount > 0 ? ` · ${unusedCount} unused` : ''}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3 flex-shrink-0">
            <div className="relative flex-1 min-w-[12rem]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search roles…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={unusedOnly}
                onChange={e => setUnusedOnly(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              Unused only
            </label>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {error && (
              <div className="m-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <div className="p-8 text-center text-sm text-gray-500">Loading roles…</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                {usage.length === 0 ? 'No roles yet' : 'No roles match your filters'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filtered.map(row => {
                  const unused = row.seatCount === 0
                  return (
                    <li
                      key={row.definition.id}
                      className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 truncate">
                            {row.definition.name}
                          </span>
                          {row.definition.isContractor && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-sky-100 text-sky-800">
                              Contractor
                            </span>
                          )}
                          {unused ? (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-gray-100 text-gray-600">
                              Unused
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-green-50 text-green-800">
                              {row.seatCount} seat{row.seatCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {(row.definition.accountabilities || []).length === 1
                            ? '1 accountability'
                            : `${(row.definition.accountabilities || []).length} accountabilities`}
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {unused ? (
                            <span className="text-xs text-gray-400">Not on any chart</span>
                          ) : (
                            row.charts.map(chart => (
                              <span
                                key={chart.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                                title={`${chart.seatCount} seat${chart.seatCount === 1 ? '' : 's'}`}
                              >
                                {chart.status === 'live' ? (
                                  <span className="text-[10px] font-semibold uppercase text-green-700">
                                    Live
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-semibold uppercase text-amber-700">
                                    Draft
                                  </span>
                                )}
                                {chart.name}
                                <span className="text-gray-400">×{chart.seatCount}</span>
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingDefinition(row.definition)
                            setEditOpen(true)
                          }}
                          className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit role"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          disabled={!unused || deletingId === row.definition.id}
                          onClick={() => handleDelete(row)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                          title={
                            unused
                              ? 'Delete role'
                              : 'Remove this role from all chart seats before deleting'
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <EditRoleDefinitionModal
        isOpen={editOpen}
        onClose={() => {
          setEditOpen(false)
          setEditingDefinition(null)
        }}
        onSaved={handleEditSaved}
        definition={editingDefinition}
      />
    </>
  )
}
